import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import Index from "./pages/Index";
import { STATIC_MIRRORED } from "./lib/i18n/registry";
import { viewFor } from "./content/pages";
import BlockPage from "./content/pages/BlockPage";
import { HtmlLangSync } from "./lib/i18n/useLocale";
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/ScrollToTop";
import Analytics from "./components/Analytics";

const MODULE_RELOAD_KEY = "eb-module-import-reload-at";
const MODULE_RELOAD_WINDOW_MS = 30_000;

const isModuleImportError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(message);
};

class ModuleLoadBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (!isModuleImportError(error)) return;

    const lastReloadAt = Number(sessionStorage.getItem(MODULE_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReloadAt > MODULE_RELOAD_WINDOW_MS) {
      sessionStorage.setItem(MODULE_RELOAD_KEY, String(Date.now()));
      window.location.reload();
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (isModuleImportError(this.state.error)) return <Loader />;
    throw this.state.error;
  }
}

/* Defer heavy providers — not needed for first paint */
const LazyProviders = lazy(() => import("./LazyProviders"));

const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const ContactThanks = lazy(() => import("./pages/ContactThanks"));
const FAQ = lazy(() => import("./pages/FAQ"));
const LegalRequirements = lazy(() => import("./pages/LegalRequirements"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Venues = lazy(() => import("./pages/Venues"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPalaces = lazy(() => import("./pages/blog/BlogPalaces"));
const BlogBeach = lazy(() => import("./pages/blog/BlogBeach"));
const Locations = lazy(() => import("./pages/Locations"));
const Services = lazy(() => import("./pages/Services"));

const LocationPages = () => import("./pages/locations/LocationPages");
const ServicePages = () => import("./pages/services/ServicePages");
const Portal = lazy(() => import("./pages/Portal"));
const ManagerPortal = lazy(() => import("./pages/ManagerPortal"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const Partners = lazy(() => import("./pages/Partners"));
const Careers = lazy(() => import("./pages/Careers"));

// Lazy wrappers for location pages
const AlgarvePage = lazy(() => import("./pages/locations/AlgarvePage"));
const LisbonPage = lazy(() => import("./pages/locations/LisbonPage"));
const CascaisPage = lazy(() => import("./pages/locations/CascaisPage"));
const SintraPage = lazy(() => import("./pages/locations/SintraPage"));
const PortoPage = lazy(() => import("./pages/locations/PortoPage"));
const DouroPage = lazy(() => import("./pages/locations/DouroPage"));
const HiddenGemsPage = lazy(() => import("./pages/locations/HiddenGemsPage"));
const ComportaPage = lazy(() => import("./pages/locations/ComportaPage"));
const AlentejoPage = lazy(() => import("./pages/locations/AlentejoPage"));
const MadeiraPage = lazy(() => import("./pages/locations/MadeiraPage"));

// Lazy wrappers for venue pages
const PestanaPalacePage = lazy(() => import("./pages/venues/PestanaPalacePage"));
const PenhaLongaPage = lazy(() => import("./pages/venues/PenhaLongaPage"));
const QueluzPage = lazy(() => import("./pages/venues/QueluzPage"));
const SeteaisPage = lazy(() => import("./pages/venues/SeteaisPage"));
const ParquesDeSintraPage = lazy(() => import("./pages/venues/ParquesDeSintraPage"));
const BelmondReidsPalacePage = lazy(() => import("./pages/venues/BelmondReidsPalacePage"));
const CasaSacotoPage = lazy(() => import("./pages/venues/CasaSacotoPage"));
const SaoLourencoDoBarrocalPage = lazy(() => import("./pages/venues/SaoLourencoDoBarrocalPage"));
const QuintaDaBellaVistaPage = lazy(() => import("./pages/venues/QuintaDaBellaVistaPage"));
const SixSensesDouroValleyPage = lazy(() => import("./pages/venues/SixSensesDouroValleyPage"));
const VidagoPalacePage = lazy(() => import("./pages/venues/VidagoPalacePage"));
const PalacioDaBolsaPage = lazy(() => import("./pages/venues/PalacioDaBolsaPage"));
const TheYeatmanPage = lazy(() => import("./pages/venues/TheYeatmanPage"));

// Lazy wrappers for service pages
const WeddingPlanningPage = lazy(() => ServicePages().then(m => ({ default: m.WeddingPlanning })));
const CreativeDirectionPage = lazy(() => ServicePages().then(m => ({ default: m.CreativeDirection })));
const EventProductionPage = lazy(() => ServicePages().then(m => ({ default: m.EventProduction })));
const WeddingCoordinationPage = lazy(() => ServicePages().then(m => ({ default: m.WeddingCoordination })));
const PrivateConcierge = lazy(() => ServicePages().then(m => ({ default: m.PrivateConcierge })));

/**
 * Ключ маршрута → компонент страницы (BTQ-47, Этап 1).
 *
 * Тот же компонент обслуживает обе локали: /services/wedding-planning/ и
 * /pt/organizacao-de-casamentos/ — это одна страница на двух языках, а не два
 * файла. Копипаста Page.tsx → PagePt.tsx, которой была сделана версия v1,
 * здесь невозможна: португальский маршрут физически не к чему подключить,
 * кроме английского компонента.
 *
 * Текст берётся из контент-слоя по локали, поэтому страница, у которой
 * португальского текста ещё нет, показывает английский. Такие пары помечены
 * planned и в прод не выходят: их нет ни в карте сайта, ни в hreflang,
 * ни в VALID_ROUTES воркера. Видны они только на preview-сборке.
 */
export const PAGE_BY_KEY: Record<string, React.ComponentType> = {
  home: Index,
  about: About,
  contact: Contact,
  faq: FAQ,
  legal: LegalRequirements,
  portfolio: Portfolio,
  venues: Venues,
  blog: Blog,
  "blog-palaces": BlogPalaces,
  "blog-beach": BlogBeach,
  locations: Locations,
  services: Services,
  privacy: PrivacyPolicy,
  terms: TermsConditions,
  partners: Partners,
  careers: Careers,
  "location-algarve": AlgarvePage,
  "location-lisbon": LisbonPage,
  "location-cascais": CascaisPage,
  "location-sintra": SintraPage,
  "location-porto": PortoPage,
  "location-douro-valley": DouroPage,
  "location-hidden-gems": HiddenGemsPage,
  "location-comporta": ComportaPage,
  "location-alentejo": AlentejoPage,
  "location-madeira": MadeiraPage,
  "service-wedding-planning": WeddingPlanningPage,
  "service-creative-direction": CreativeDirectionPage,
  "service-event-production": EventProductionPage,
  "service-wedding-coordination": WeddingCoordinationPage,
  "service-private-concierge": PrivateConcierge,
  "venue-pestana-palace": PestanaPalacePage,
  "venue-penha-longa-resort": PenhaLongaPage,
  "venue-palacio-de-queluz": QueluzPage,
  "venue-palacio-de-seteais": SeteaisPage,
  "venue-parques-de-sintra": ParquesDeSintraPage,
  "venue-belmond-reids-palace": BelmondReidsPalacePage,
  "venue-casa-sacoto": CasaSacotoPage,
  "venue-sao-lourenco-do-barrocal": SaoLourencoDoBarrocalPage,
  "venue-quinta-da-bella-vista": QuintaDaBellaVistaPage,
  "venue-six-senses-douro-valley": SixSensesDouroValleyPage,
  "venue-vidago-palace": VidagoPalacePage,
  "venue-palacio-da-bolsa": PalacioDaBolsaPage,
  "venue-the-yeatman": TheYeatmanPage,
};

const Loader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: "#ffffff" }}>
    <p
      style={{
        fontFamily: "'Century Gothic', 'Futura', 'Montserrat', sans-serif",
        fontWeight: 700,
        fontSize: "15px",
        letterSpacing: "3px",
        textTransform: "uppercase" as const,
        color: "#000000",
        margin: 0,
      }}
    >
      EVENT BOUTIQUE
    </p>
    <div
      style={{
        width: "60px",
        height: "1px",
        marginTop: "14px",
        backgroundColor: "#c1a1a4",
        animation: "loaderExpand 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite",
        transformOrigin: "center",
      }}
    />
    <style>{`
      @keyframes loaderExpand {
        0% { transform: scaleX(0.15); opacity: 0.4; }
        50% { transform: scaleX(1); opacity: 1; }
        100% { transform: scaleX(0.15); opacity: 0.4; }
      }
    `}</style>
  </div>
);

/**
 * AppInner is exported separately so the build-time prerender entry
 * (src/prerender/entry-server.tsx, BTQ-17) can render the exact same tree
 * under a StaticRouter in Node. Browser behaviour unchanged: the default
 * export below composes LazyMotion > BrowserRouter > inner as before.
 * When ADDING A ROUTE below, also add it to PRERENDER_ROUTES in
 * src/prerender/entry-server.tsx (unless it is /portal*, a Navigate
 * redirect, or the catch-all).
 */
export const AppInner = () => (
  <>
    <ScrollToTop />
    {/* Держит <html lang> в согласии с языком страницы при переходах
        внутри приложения (BTQ-47). */}
    <HtmlLangSync />
      <ModuleLoadBoundary>
        <Suspense fallback={<Loader />}>
          <Suspense fallback={null}>
            <LazyProviders />
          </Suspense>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about-us/" element={<About />} />
            <Route path="/contact/" element={<Contact />} />
            {/* TK-823: «спасибо» после отправки формы (EN и PT). noindex, вне реестра
                зеркала и PRERENDER_ROUTES: служебный экран, не страница для поиска.
                PT-адрес пропускает functions/pt/[[path]].ts. */}
            <Route path="/contact/thank-you/" element={<ContactThanks />} />
            <Route path="/pt/contactos/obrigado/" element={<ContactThanks />} />
            <Route path="/faq/" element={<FAQ />} />
            <Route path="/legal-requirements/" element={<LegalRequirements />} />
            <Route path="/portfolio/" element={<Portfolio />} />
            <Route path="/venues/" element={<Venues />} />
            <Route path="/blog/" element={<Blog />} />
            <Route path="/blog/top-palaces-portugal/" element={<BlogPalaces />} />
            <Route path="/blog/romantic-beach-locations/" element={<BlogBeach />} />
            <Route path="/locations/" element={<Locations />} />
            <Route path="/locations/algarve/" element={<AlgarvePage />} />
            <Route path="/locations/lisbon/" element={<LisbonPage />} />
            <Route path="/locations/cascais/" element={<CascaisPage />} />
            <Route path="/locations/sintra/" element={<SintraPage />} />
            <Route path="/locations/porto/" element={<PortoPage />} />
            <Route path="/locations/douro-valley/" element={<DouroPage />} />
            <Route path="/locations/hidden-gems/" element={<HiddenGemsPage />} />
            <Route path="/locations/comporta/" element={<ComportaPage />} />
            <Route path="/locations/alentejo/" element={<AlentejoPage />} />
            <Route path="/locations/madeira/" element={<MadeiraPage />} />
            <Route path="/services/" element={<Services />} />
            <Route path="/services/wedding-planning/" element={<WeddingPlanningPage />} />
            <Route path="/services/creative-direction/" element={<CreativeDirectionPage />} />
            <Route path="/services/event-production/" element={<EventProductionPage />} />
            <Route path="/services/wedding-coordination/" element={<WeddingCoordinationPage />} />
            <Route path="/services/private-concierge/" element={<PrivateConcierge />} />
            <Route path="/venues/pestana-palace/" element={<PestanaPalacePage />} />
            <Route path="/venues/penha-longa-resort/" element={<PenhaLongaPage />} />
            <Route path="/venues/palacio-de-queluz/" element={<QueluzPage />} />
            <Route path="/venues/palacio-de-seteais/" element={<SeteaisPage />} />
            <Route path="/venues/parques-de-sintra/" element={<ParquesDeSintraPage />} />
            <Route path="/venues/casa-sacoto/" element={<CasaSacotoPage />} />
            <Route path="/venues/sao-lourenco-do-barrocal/" element={<SaoLourencoDoBarrocalPage />} />
            <Route path="/venues/quinta-da-bella-vista/" element={<QuintaDaBellaVistaPage />} />
            <Route path="/venues/six-senses-douro-valley/" element={<SixSensesDouroValleyPage />} />
            <Route path="/venues/belmond-reids-palace/" element={<BelmondReidsPalacePage />} />
            <Route path="/venues/reids-palace/" element={<Navigate to="/venues/belmond-reids-palace/" replace />} />
            <Route path="/venues/reids-palace" element={<Navigate to="/venues/belmond-reids-palace/" replace />} />
            <Route path="/venues/vidago-palace/" element={<VidagoPalacePage />} />
            <Route path="/privacy-policy/" element={<PrivacyPolicy />} />
            {/* Manager console — MUST come before the client /portal route
                otherwise React Router would match /portal/* first and the
                manager surface would never mount. */}
            <Route path="/portal/manager" element={<ManagerPortal />} />
            <Route path="/portal/manager/" element={<ManagerPortal />} />
            <Route path="/portal/" element={<Portal />} />
            <Route path="/terms/" element={<TermsConditions />} />
            <Route path="/partners/" element={<Partners />} />
            <Route path="/careers/" element={<Careers />} />
            <Route path="/venues/palacio-da-bolsa/" element={<PalacioDaBolsaPage />} />
            <Route path="/venues/palacio-da-bolsa" element={<Navigate to="/venues/palacio-da-bolsa/" replace />} />
            <Route path="/venues/the-yeatman/" element={<TheYeatmanPage />} />

            {/* Redirects: old site URLs found in Google Search Console → current structure */}
            {/* "Page with redirect" (2) — old blog tag pages */}
            <Route path="/blog/tag/*" element={<Navigate to="/blog/" replace />} />

            {/* Trailing-slash normalisation */}
            <Route path="/venues" element={<Navigate to="/venues/" replace />} />
            <Route path="/blog" element={<Navigate to="/blog/" replace />} />
            <Route path="/contact" element={<Navigate to="/contact/" replace />} />
            <Route path="/careers" element={<Navigate to="/careers/" replace />} />

            {/* "Crawled – currently not indexed" — old site structure */}
            <Route path="/our-weddings" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/our-weddings/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/wedding-venues" element={<Navigate to="/venues/" replace />} />
            <Route path="/wedding-venues/" element={<Navigate to="/venues/" replace />} />
            <Route path="/porto" element={<Navigate to="/locations/porto/" replace />} />
            <Route path="/porto/" element={<Navigate to="/locations/porto/" replace />} />
            <Route path="/afonso" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/afonso/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/miron-1" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/miron-1/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/incentive-trips" element={<Navigate to="/services/" replace />} />
            <Route path="/incentive-trips/" element={<Navigate to="/services/" replace />} />
            <Route path="/gide-trips" element={<Navigate to="/services/" replace />} />
            <Route path="/gide-trips/" element={<Navigate to="/services/" replace />} />
            <Route path="/sunrise-city-wedding" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/sunrise-city-wedding/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/theme-and-seasonal-parties" element={<Navigate to="/services/" replace />} />
            <Route path="/theme-and-seasonal-parties/" element={<Navigate to="/services/" replace />} />
            <Route path="/modern-and-romantic-wedding" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/modern-and-romantic-wedding/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/shop/cart" element={<Navigate to="/" replace />} />
            <Route path="/shop/cart/" element={<Navigate to="/" replace />} />
            <Route path="/RelaxedStyleWedding" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/RelaxedStyleWedding/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/classic-wedding-in-portuguese-countryside" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/classic-wedding-in-portuguese-countryside/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/loreal-award-ceremony" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/loreal-award-ceremony/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/decor" element={<Navigate to="/services/" replace />} />
            <Route path="/decor/" element={<Navigate to="/services/" replace />} />

            {/* From Google Search Console: old URLs returning noindex via NotFound */}
            <Route path="/gallery" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/gallery/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/elopement" element={<Navigate to="/services/" replace />} />
            <Route path="/elopement/" element={<Navigate to="/services/" replace />} />

            {/* From GSC 404 report — old site URLs */}
            <Route path="/wedding-packages" element={<Navigate to="/services/" replace />} />
            <Route path="/wedding-packages/" element={<Navigate to="/services/" replace />} />
            <Route path="/birthdays" element={<Navigate to="/services/" replace />} />
            <Route path="/birthdays/" element={<Navigate to="/services/" replace />} />
            <Route path="/intermarche-product-launch" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/intermarche-product-launch/" element={<Navigate to="/portfolio/" replace />} />
            <Route path="/corporate-new" element={<Navigate to="/services/" replace />} />
            <Route path="/corporate-new/" element={<Navigate to="/services/" replace />} />
            <Route path="/wedding-decor" element={<Navigate to="/services/creative-direction/" replace />} />
            <Route path="/wedding-decor/" element={<Navigate to="/services/creative-direction/" replace />} />
            <Route path="/cliff-top-seaside-venue" element={<Navigate to="/blog/romantic-beach-locations/" replace />} />
            <Route path="/cliff-top-seaside-venue/" element={<Navigate to="/blog/romantic-beach-locations/" replace />} />
            <Route path="/childrens-events" element={<Navigate to="/services/" replace />} />
            <Route path="/childrens-events/" element={<Navigate to="/services/" replace />} />

            {/* From Google search: old blog post slugs */}
            <Route path="/contacts" element={<Navigate to="/contact/" replace />} />
            <Route path="/contacts/" element={<Navigate to="/contact/" replace />} />
            <Route path="/blog/portugals-7-most-romantic-beach-ceremony-locations" element={<Navigate to="/blog/romantic-beach-locations/" replace />} />
            <Route path="/blog/portugals-7-most-romantic-beach-ceremony-locations/" element={<Navigate to="/blog/romantic-beach-locations/" replace />} />
            <Route path="/blog/top-20-classy-luxury-venues-that-will-amaze-your-guests" element={<Navigate to="/blog/top-palaces-portugal/" replace />} />
            <Route path="/blog/top-20-classy-luxury-venues-that-will-amaze-your-guests/" element={<Navigate to="/blog/top-palaces-portugal/" replace />} />

            {/* Португальская сторона зеркала (BTQ-47). Маршруты берутся из
                реестра, и только для состояния mirrored: роут не существует,
                пока не существует контент. Пустой или тонкий двойник хуже
                отсутствия — ровно за это версия v1 отправилась в откат.

                Вид страницы берётся по локали: тот же компонент, что у
                английской версии, либо собственная схема блоков, если форма
                португальской страницы отличается (см. src/content/pages). */}
            {STATIC_MIRRORED.map((entry) => {
              const view = viewFor(entry.key, "pt");
              const path = entry.pt as string;
              if (view.kind === "blocks") {
                return (
                  <Route
                    key={path}
                    path={path}
                    element={<BlockPage routeKey={entry.key} meta={view.meta} blocks={view.blocks} />}
                  />
                );
              }
              const Page = PAGE_BY_KEY[entry.key];
              return Page ? <Route key={path} path={path} element={<Page />} /> : null;
            })}

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ModuleLoadBoundary>
    <Analytics />
  </>
);

const App = () => (
  <LazyMotion features={domAnimation}>
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  </LazyMotion>
);

export default App;
